import _values from 'lodash/values';
import _countBy from 'lodash/countBy';
import shapeMetadata from '../../components/configurator/shapes/index';
import {
  area,
  finishedEdgeLength,
  finishedCornerCount,
  backsplashArea,
} from '../../components/configurator/shapes/util';

/** Compute the area of backsplash(es) associated with the edges of a shape for Post Form Laminate */
export const backsplashAreaForPostForm = ({ edges }) => {
  return _values(edges).reduce((acc, edge) =>
    acc + (edge.hasBacksplash && !edge.isEndSplash ? edge.backsplashHeight * edge.length : 0), 0);
};

/** Compute the area of endsplash(es) associated with the edges of a shape for Post Form Laminate */
export const endsplashAreaForPostForm = ({ edges }) => {
  return _values(edges).reduce((acc, edge) =>
    acc + (edge.hasBacksplash && edge.isEndSplash ? edge.backsplashHeight * edge.length : 0), 0);
};

/**
 * Counts the number of end splashes in the given edges.
 * @param {Object} edges - The edges object containing edge data.
 * @returns {number} The count of edges marked as end splashes.
 */
export const endSplashCountForPostForm = ({ edges }) => {
  return _countBy(_values(edges), (edge) => edge.isEndSplash).true || 0;
};

/**
 * Calculates various laminate-related values for post-form shapes.
 * @param {Array<Object>} shapes - An array of shape objects to process.
 * @returns {Object} An object containing calculated values:
 * - countertopArea: Total countertop area (in square feet).
 * - backsplashArea: Total backsplash area (in square feet).
 * - totalArea: Total area (in square feet).
 * - totalFinishedEdgeLength: Total finished edge length (in feet).
 * - totalExteriorCorners: Total number of exterior corners.
 * - totalInteriorCorners: Total number of interior corners.
 * - totalEndSplashes: Total number of end splashes.
 */
export const calculateChoiceValuesPostFormLaminate = (shapes) => {
  let totalBsArea = 0;
  let totalArea = 0;
  let totalFinishedEdgeLength = 0;
  let totalExteriorCorners = 0;
  let totalInteriorCorners = 0;
  let totalEndSplashes = 0;

  for (let i = 0, len = shapes.length; i < len; i += 1) {
    const shape = shapes[i];
    const meta = shapeMetadata[shape.type];

    const poly = meta.toPolygon(shape);
    const shapeCtArea = area(poly);
    const shapeBsArea = backsplashAreaForPostForm(shape); // For Post Form DO NOT include end splashes
    const endSplashes = endSplashCountForPostForm(shape);
    totalBsArea += Math.ceil(shapeBsArea / 144);
    totalEndSplashes += endSplashes;
    shape.area = Math.ceil(shapeCtArea / 144) + Math.ceil(shapeBsArea / 144);
    totalFinishedEdgeLength += finishedEdgeLength(shape, poly, meta) / 12;
    totalArea += shape.area;
    const { interior, exterior } = finishedCornerCount(shape, poly, meta);
    totalExteriorCorners += exterior;
    totalInteriorCorners += interior;
  }

  return {
    countertopArea: totalArea,
    backsplashArea: totalBsArea,
    totalArea,
    totalFinishedEdgeLength: Math.ceil(totalFinishedEdgeLength),
    totalExteriorCorners,
    totalInteriorCorners,
    totalEndSplashes,
  };
};

/**
 * Calculates various laminate-related values for custom shapes.
 * @param {Array<Object>} shapes - An array of shape objects to process.
 * @returns {Object} An object containing calculated values:
 * - countertopArea: Total countertop area (in square feet).
 * - backsplashArea: Total backsplash area (in square feet).
 * - totalArea: Total area (in square feet).
 * - totalFinishedEdgeLength: Total finished edge length (in feet).
 * - totalExteriorCorners: Total number of exterior corners.
 * - totalInteriorCorners: Total number of interior corners.
 */
export const calculateChoiceValuesCustomLaminate = (shapes) => {
  let totalCtArea = 0;
  let totalBsArea = 0;
  let totalFinishedEdgeLength = 0;
  let totalExteriorCorners = 0;
  let totalInteriorCorners = 0;

  for (let i = 0, len = shapes.length; i < len; i += 1) {
    const shape = shapes[i];
    const meta = shapeMetadata[shape.type];

    const poly = meta.toPolygon(shape);
    const shapeCtArea = area(poly);
    const shapeBsArea = backsplashArea(shape);
    totalCtArea += Math.ceil(shapeCtArea / 144);
    totalBsArea += Math.ceil(shapeBsArea / 144);
    shape.area = Math.ceil(shapeCtArea / 144);
    totalFinishedEdgeLength += finishedEdgeLength(shape, poly, meta) / 12;

    const { interior, exterior } = finishedCornerCount(shape, poly, meta);
    totalExteriorCorners += exterior;
    totalInteriorCorners += interior;
  }

  return {
    countertopArea: totalCtArea,
    backsplashArea: totalBsArea,
    totalArea: totalCtArea,
    totalFinishedEdgeLength: Math.ceil(totalFinishedEdgeLength),
    totalExteriorCorners,
    totalInteriorCorners,
  };
};
